'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, BookOpen, FileText } from 'lucide-react';
import { useArticles } from '@/context/ArticlesContext';

export default function MaterialsHub() {
  const { articles, categories, setSelectedArticleId } = useArticles();

  // Find the Materials & Finishes category
  const materialsCategory = categories?.find(
    c => c?.name?.toLowerCase()?.includes('material') || c?.id === 'cat-materials'
  );

  // Get only published articles in the materials category
  const materialArticles = materialsCategory
    ? articles?.filter(a => a?.published && a?.category === materialsCategory?.id)
    : [];

  if (!materialsCategory) return null;

  return (
    <section>
      {/* Section header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="section-label mb-1">Product Knowledge</p>
          <h2 className="font-serif text-2xl font-semibold text-foreground">
            {materialsCategory?.icon} {materialsCategory?.name}
          </h2>
          {materialsCategory?.description && (
            <p className="text-sm text-muted-foreground mt-1">{materialsCategory?.description}</p>
          )}
        </div>
        <Link
          href={`/section-article-list-page?category=${materialsCategory?.id}`}
          className="hidden sm:flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors font-medium"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {materialArticles?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-xl">
          <BookOpen className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No articles published yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            Articles added to the &ldquo;{materialsCategory?.name}&rdquo; category will appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {materialArticles?.map(article => (
            <Link
              key={article?.id}
              href="/article-material-detail-page"
              onClick={() => setSelectedArticleId(article?.id)}
              className="group card-luxury p-5 rounded-xl flex flex-col gap-3 hover:border-gold/40 transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground leading-snug group-hover:text-gold transition-colors line-clamp-2">
                    {article?.title}
                  </h3>
                  {article?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {article?.tags?.slice(0, 2)?.map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-2 border border-border text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                {article?.content?.replace(/^#+\s+.+$/gm, '')?.replace(/[*_`>-]/g, '')?.trim()?.slice(0, 120)}...
              </p>
              <div className="flex items-center gap-1 text-xs text-gold mt-auto">
                <span>Read article</span>
                <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}